// Offline identity — who was signed in on this device, kept separately from
// the login TOKENS. Tokens exist to talk to the server; this exists so the app
// can open to the user's own local work (pilot slip, voyage plan, drafts) when
// there is no server to talk to. Written to localStorage AND the IndexedDB
// durable mirror, because iOS discarded the localStorage login while leaving
// everything else intact (Jill's iPad, 2026-07-11).

import { durableGet, durableSet, durableRemove } from "./durableStore";

const KEY = "seapa_offline_identity";

export async function persistOfflineIdentity(identity) {
  try {
    const raw = JSON.stringify(identity);
    try { localStorage.setItem(KEY, raw); } catch { /* quota */ }
    await durableSet(KEY, raw);
  } catch { /* backup layer never throws */ }
}

export async function readOfflineIdentity() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.userId) return parsed;
    }
  } catch { /* fall through to the durable copy */ }
  const raw = await durableGet(KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.userId) {
      try { localStorage.setItem(KEY, raw); } catch { /* quota */ }
      return parsed;
    }
  } catch { /* corrupt */ }
  return null;
}

export async function clearOfflineIdentity() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  await durableRemove(KEY);
}

// "This device was signed out on purpose" (Jill 2026-09-24). Sign Out keeps
// the offline identity above, so a signed-out device can still open its own
// saved briefs from the login page's Continue offline button while there is no
// internet. This flag is what stops the app from quietly booting back in on
// the next launch: only a tap on Continue offline, or a real sign-in, gets
// past it. Kept in localStorage AND the IndexedDB mirror, like the identity.
const SIGNED_OUT_KEY = "seapa_signed_out";

export function markSignedOut() {
  try { localStorage.setItem(SIGNED_OUT_KEY, "1"); } catch { /* quota */ }
  durableSet(SIGNED_OUT_KEY, "1");
}

export function clearSignedOut() {
  try { localStorage.removeItem(SIGNED_OUT_KEY); } catch { /* ignore */ }
  durableRemove(SIGNED_OUT_KEY);
}

// Synchronous first read, for initial React state.
export function isSignedOutSync() {
  try { return localStorage.getItem(SIGNED_OUT_KEY) === "1"; } catch { return false; }
}

// restore: put the quick localStorage copy back when only IndexedDB has it
// (iOS dropped localStorage). Only AuthContext restores, and it guards
// against a sign-in that beat the read; other callers just ask (Codex #506).
export async function isSignedOut({ restore = false } = {}) {
  if (isSignedOutSync()) return true;
  if ((await durableGet(SIGNED_OUT_KEY)) !== "1") return false;
  if (restore) { try { localStorage.setItem(SIGNED_OUT_KEY, "1"); } catch { /* quota */ } }
  return true;
}
